import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// ---- 任务类型 ----

export type ModalTaskType =
  | "stop_service"
  | "download_gate"
  | "login_remind"
  | "login_pop_queue"
  | "invite_wheel_entry"
  | "install_prompt"
  | "first_deposit"
  | "activity_popup"
  | "invite_popup";

export interface TaskDef {
  /** 唯一标识，同时用于日志 */
  id: string;
  /** 弹窗类型，用于查找 executor */
  type: ModalTaskType;
  /** 优先级：数值越高越先执行，相同优先级按入队顺序 FIFO */
  priority: number;
  /** 入队序号（reducer 自动分配） */
  seq: number;
  /** 去重 key：队列中已有相同 key 则拒绝入队 */
  dedupeKey?: string;
  /** 当前 Web 运行内去重，整页刷新开始新一轮 */
  sessionOnceKey?: string;
  /** 可序列化的弹窗数据 */
  payload: Record<string, any>;
}

export interface TaskResult {
  id: string;
  type: string;
  result: "completed" | "skipped";
  reason?: string;
  at: number;
}

// ---- 入队 payload（不含 seq，reducer 自动分配）----

export type EnqueuePayload = Omit<TaskDef, "seq">;

// ---- State ----

export interface OrchestratorState {
  /** 待执行任务队列（按 priority desc, seq asc 排序） */
  queue: TaskDef[];
  /** 被当前业务/页面状态阻挡的普通任务，等待恢复 */
  deferredGating: TaskDef[];
  /** 正在执行的任务 */
  currentTask: TaskDef | null;
  /** 排水状态 */
  status: "idle" | "draining";
  /** 入队序号计数器 */
  seq: number;
  /** 执行历史（用于 DevTools 可视化） */
  history: TaskResult[];
  /** 主动进入业务后，本轮不再接受剩余/重试产生的营销任务 */
  marketingCancelled: boolean;
}

const initialState: OrchestratorState = {
  queue: [],
  deferredGating: [],
  currentTask: null,
  status: "idle",
  seq: 0,
  history: [],
  marketingCancelled: false,
};

// ---- 内部工具 ----

const sortQueue = (queue: TaskDef[]) => {
  queue.sort((a, b) => {
    if (a.priority !== b.priority) return b.priority - a.priority;
    return a.seq - b.seq;
  });
};

// ---- Slice ----

const modalOrchestratorSlice = createSlice({
  name: "modalOrchestrator",
  initialState,
  reducers: {
    /**
     * 将任务加入队列。
     * 包含 dedupeKey 入队时去重检查（sessionOnce 检查在排水循环运行时执行）。
     * 如果当前 status 为 idle，自动切换为 draining。
     */
    enqueue(state, action: PayloadAction<EnqueuePayload>) {
      const task = action.payload;
      if (
        state.marketingCancelled &&
        task.priority <= 20 &&
        task.type !== "login_remind"
      )
        return;

      // dedupeKey 检查：queue + deferredGating 中已有相同 key 则跳过
      if (task.dedupeKey) {
        const existsInQueue = state.queue.some(
          (t) => t.dedupeKey === task.dedupeKey
        );
        const existsInDeferredGating = state.deferredGating.some(
          (t) => t.dedupeKey === task.dedupeKey
        );
        const existsInCurrentTask =
          state.currentTask?.dedupeKey === task.dedupeKey;
        if (existsInQueue || existsInDeferredGating || existsInCurrentTask)
          return;
      }

      // sessionOnce 检查已移至排水循环运行时（useOrchestratorDrain.ts），
      // 避免在 reducer 中产生副作用。

      const seq = ++state.seq;
      state.queue.push({ ...task, seq } as TaskDef);
      sortQueue(state.queue);

      // 自动触发排水
      if (state.status === "idle") {
        state.status = "draining";
      }
    },

    /**
     * 从队首取出任务设为 currentTask。
     */
    dequeueAndSetCurrent(state) {
      if (state.currentTask || !state.queue.length) return;
      state.currentTask = state.queue.shift()!;
    },

    /** 呈现组件真正卸载时保留未完成任务；重挂载继续，不创建新批次。 */
    requeueCurrentTask(state) {
      if (!state.currentTask) return;
      state.queue.push(state.currentTask);
      state.currentTask = null;
      sortQueue(state.queue);
    },

    /**
     * 当前任务完成。
     */
    completeCurrentTask(state) {
      if (!state.currentTask) return;
      state.history.push({
        id: state.currentTask.id,
        type: state.currentTask.type,
        result: "completed",
        at: Date.now(),
      });
      state.currentTask = null;
    },

    /**
     * 跳过当前任务。
     */
    skipCurrentTask(state, action: PayloadAction<string>) {
      if (!state.currentTask) return;
      state.history.push({
        id: state.currentTask.id,
        type: state.currentTask.type,
        result: "skipped",
        reason: action.payload,
        at: Date.now(),
      });
      state.currentTask = null;
    },

    /**
     * 普通公告/安装/营销暂缓，维护和强制下载仍保持独立策略。
     */
    deferTasksToGating(state) {
      const deferred: TaskDef[] = [];
      const remaining: TaskDef[] = [];
      for (const t of state.queue) {
        if (t.priority <= 40) {
          deferred.push(t);
        } else {
          remaining.push(t);
        }
      }
      state.queue = remaining;
      state.deferredGating.push(...deferred);
    },

    /**
     * 将 deferredGating 任务移回 queue，重新排序并触发排水。
     * 当门控解除时由事件触发调用。
     */
    restoreDeferredGating(state) {
      if (!state.deferredGating.length) return;
      state.queue.push(...state.deferredGating);
      state.deferredGating = [];
      sortQueue(state.queue);
      if (state.status === "idle") {
        state.status = "draining";
      }
    },

    /**
     * 用户从营销弹窗主动进入其它面板/页面时，丢弃尚未展示的 P20 营销任务。
     * 当前任务仍由自身 resolve/complete 结束。
     */
    cancelQueuedP20Tasks(state) {
      state.marketingCancelled = true;
      state.queue = state.queue.filter((task) => task.priority > 20);
      state.deferredGating = state.deferredGating.filter(
        (task) => task.priority > 20
      );
    },

    /**
     * 排水完成，状态回到 idle。
     */
    drainFinished(state) {
      state.status = "idle";
      state.currentTask = null;
    },

    /**
     * 登出或重置时清空所有状态。
     */
    reset() {
      return { ...initialState };
    },
  },
});

export const {
  enqueue,
  dequeueAndSetCurrent,
  requeueCurrentTask,
  completeCurrentTask,
  skipCurrentTask,
  deferTasksToGating,
  restoreDeferredGating,
  cancelQueuedP20Tasks,
  drainFinished,
  reset,
} = modalOrchestratorSlice.actions;

export default modalOrchestratorSlice.reducer;
